import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormFullWidth,
  FormField,
  FormTextField,
  FormSelect,
  FormFileUpload,
  FormActions,
  FormCancelButton,
  FormSubmitButton,
} from '../../Components/Form';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { COLORS } from '../../Styles/Colors';
import { DownloadIcon, ImageIcon, UploadIcon } from 'lucide-react';
import apiClient from '../../Services/ApiClient';
import apiClientFormData from '../../Services/ApiClientFormData';
import { useSnackbar } from '../../Context/SnackbarProvider';

/**
 * Non-ABATIX "Add Inventory Item" form and "Bulk Import" panel.
 * Moved here unchanged from the old Non-ABATIX inventory page so the
 * Non-ABATIX menu's Add / Import page can use them.
 */

const ImportStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const ImportStepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  color: ${COLORS.darkGray};
  font-size: 1rem;
  font-weight: 600;
`;

const ImportInstructionText = styled.p`
  margin: 0;
  color: ${COLORS.darkGray};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  background-color: ${COLORS.white};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.orange};
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  line-height: 1.8;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }
`;

const FileDropZone = styled.label<{ $hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 20px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : COLORS.gray)};
  border-radius: 8px;
  background-color: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const SelectedFileName = styled.span`
  color: ${COLORS.teal};
  font-size: 0.9rem;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`;

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 560px;
`;

const ImageUploadText = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ImageUploadSubtext = styled.span`
  color: ${COLORS.lightGray};
  font-size: 0.75rem;
  text-align: center;
`;


// ─── Add Item Form ──────────────────────────────────────────────────────────

export interface AddFormData {
  location_id: string;
  item_id: string;
  description: string;
  category_id: string;
  price: string;
  unit_of_measure: string;
  quantity: string;
  quantity_minimum: string;
  quantity_maximum: string;
  image: File | null;
}

export const AddItemForm: React.FC<{
  ovs: any[];
  onSubmit: (data: AddFormData) => Promise<void>;
  onCancel: () => void;
}> = ({ ovs, onSubmit, onCancel }) => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [formData, setFormData] = useState<AddFormData>({
    location_id: '',
    item_id: '',
    description: '',
    category_id: '',
    price: '',
    unit_of_measure: '',
    quantity: '',
    quantity_minimum: '',
    quantity_maximum: '',
    image: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [categories, , ,] = useGetCustomData(
    formData.location_id ? `/api/non-abatix/categories?location_id=${formData.location_id}` : ''
  );

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const qty = formData.quantity !== '' ? Number(formData.quantity) : null;
    const minQty = formData.quantity_minimum !== '' ? Number(formData.quantity_minimum) : null;
    const maxQty = formData.quantity_maximum !== '' ? Number(formData.quantity_maximum) : null;

    if (qty !== null && qty < 0) {
      errors.quantity = 'Quantity cannot be negative';
    }
    if (minQty !== null && minQty < 0) {
      errors.quantity_minimum = 'Min QTY cannot be negative';
    }
    if (maxQty !== null && maxQty < 0) {
      errors.quantity_maximum = 'Max QTY cannot be negative';
    }
    if (minQty !== null && maxQty !== null && minQty > maxQty) {
      errors.quantity_minimum = 'Min cannot exceed max';
      errors.quantity_maximum = 'Max must be greater than min';
    }
    if (qty !== null && minQty !== null && qty < minQty) {
      errors.quantity = `Quantity must be at least ${minQty}`;
    }
    if (qty !== null && maxQty !== null && qty > maxQty) {
      errors.quantity = `Quantity cannot exceed ${maxQty}`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (newLocation: string | number) => {
    const locationId = String(newLocation);
    setSelectedLocation(locationId);
    setFormData(prev => ({ ...prev, location_id: locationId, category_id: '' }));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, category_id: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, image: null }));
  };

  const isFormValid =
    formData.location_id &&
    formData.item_id.trim() &&
    formData.description.trim() &&
    formData.category_id &&
    formData.quantity !== '' &&
    Object.keys(fieldErrors).length === 0;

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title="Add Inventory Item"
      subtitle="Create a new Non-Abatix inventory item with stock levels and optional image."
      submitLabel="Save Item"
      onCancel={onCancel}
      isLoading={submitting}
      disableSubmit={!isFormValid}
    >
      <FormSection>
        <SectionTitle>Location</SectionTitle>
        <SectionDescription>
          Select the warehouse or job site where this item will be stocked.
        </SectionDescription>
        <FormField label="Location" htmlFor="add-location" required>
          <LocationSelect
            id="add-location"
            ovs={ovs}
            selectedOvs={selectedLocation}
            defaultName="Select Location"
            required
            onChange={handleLocationChange}
            sx={{ width: '100%' }}
          />
        </FormField>
      </FormSection>

      <FormSection>
        <SectionTitle>Item Details</SectionTitle>
        <SectionDescription>
          Enter the part number, description, and category for this inventory item.
        </SectionDescription>
        <FormGrid>
          <FormTextField
            label="Part Number"
            name="item_id"
            value={formData.item_id}
            onChange={handleChange}
            required
            placeholder="e.g. ABC-12345"
          />
          <FormSelect
            id="category_id"
            label="Category"
            name="category_id"
            value={formData.category_id}
            onChange={handleCategoryChange}
            required
            disabled={!formData.location_id}
            placeholder={!formData.location_id ? 'Select a location first' : 'Select a Category'}
          >
            {Array.isArray(categories) && categories.map((cat: any) => (
              <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
            ))}
          </FormSelect>
          <FormFullWidth>
            <FormTextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Brief description of the item"
            />
          </FormFullWidth>
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Pricing &amp; Units</SectionTitle>
        <SectionDescription>
          Optional pricing and unit of measure for ordering and reporting.
        </SectionDescription>
        <FormGrid>
          <FormTextField
            label="Unit Price"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            placeholder="0.00"
            inputProps={{ min: 0, step: '0.01' }}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
            InputProps={{ startAdornment: <span style={{ marginRight: 4, color: COLORS.darkGray }}>$</span> }}
          />
          <FormTextField
            label="Unit of Measure"
            name="unit_of_measure"
            value={formData.unit_of_measure}
            onChange={handleChange}
            placeholder="e.g. EA, BX, FT"
            helperText="Max 5 characters (e.g. EA, BX, FT)"
            inputProps={{ maxLength: 5 }}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Stock Levels</SectionTitle>
        <SectionDescription>
          Set the current quantity and optional min/max thresholds for reorder alerts.
        </SectionDescription>
        <FormGrid>
          <FormFullWidth>
            <FormTextField
              label="Current Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                handleChange(e);
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.quantity;
                  return next;
                });
              }}
              onBlur={validateForm}
              required
              error={!!fieldErrors.quantity}
              helperText={fieldErrors.quantity || ''}
              inputProps={{ min: 0 }}
              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
            />
          </FormFullWidth>
          <FormTextField
            label="Minimum Quantity"
            name="quantity_minimum"
            type="number"
            value={formData.quantity_minimum}
            onChange={(e) => {
              handleChange(e);
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.quantity_minimum;
                delete next.quantity_maximum;
                return next;
              });
            }}
            onBlur={validateForm}
            placeholder="Reorder alert threshold"
            error={!!fieldErrors.quantity_minimum}
            helperText={fieldErrors.quantity_minimum || 'Triggers low-stock alert'}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
          />
          <FormTextField
            label="Maximum Quantity"
            name="quantity_maximum"
            type="number"
            value={formData.quantity_maximum}
            onChange={(e) => {
              handleChange(e);
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.quantity_minimum;
                delete next.quantity_maximum;
                return next;
              });
            }}
            onBlur={validateForm}
            placeholder="Shelf capacity"
            error={!!fieldErrors.quantity_maximum}
            helperText={fieldErrors.quantity_maximum || 'Used to calculate % full'}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Product Image</SectionTitle>
        <SectionDescription>Optional image for mobile catalog display.</SectionDescription>
        <FormFileUpload
          accept="image/*"
          fileName={formData.image?.name}
          placeholder="Click to upload an image"
          subtext="PNG or JPG recommended"
          previewUrl={imagePreview}
          icon={formData.image ? <ImageIcon size={32} /> : <UploadIcon size={32} />}
          onChange={(file) => {
            if (file) {
              setFormData((prev) => ({ ...prev, image: file }));
              setImagePreview(URL.createObjectURL(file));
            }
          }}
          onRemove={handleRemoveImage}
        />
      </FormSection>
    </Form>
  );
};

// ─── Bulk Import Component ──────────────────────────────────────────────────

export const BulkImportComponent: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient('/api/non-abatix/download-template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NonAbatix-Inventory-Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClientFormData('/api/non-abatix/import', { method: 'POST', body: formData });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Import failed');
      }
      const result = await response.json();
      const msg = result.errors?.length
        ? `Imported ${result.imported} item(s). ${result.errors.length} row(s) had errors.`
        : `Successfully imported ${result.imported} item(s)`;
      showSnackbar(msg, result.errors?.length ? 'error' : 'success');
      if (result.imported > 0) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <ImportWrapper>
      <ImportContent>
        <ImportInstructionText>
          Import multiple inventory items at once using an Excel spreadsheet.
          Download the template first to ensure your file has the correct column format.
        </ImportInstructionText>

        <ColumnList>
          <li><strong>Location</strong> — select from dropdown (OVID-Name)</li>
          <li><strong>Part No.</strong> — unique item identifier</li>
          <li><strong>Description</strong> — item name or description</li>
          <li><strong>Category Name</strong> — must match an existing category</li>
          <li><strong>Price</strong> — unit price (optional)</li>
          <li><strong>UOM</strong> — unit of measure (optional)</li>
          <li><strong>QTY</strong> — starting quantity</li>
          <li><strong>MIN</strong> — minimum quantity / low-stock threshold (optional)</li>
          <li><strong>MAX</strong> — maximum / reorder target quantity (optional)</li>
          <li><strong>Web Image Address</strong> — image URL to download (optional)</li>
        </ColumnList>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>1</StepNumber>
            <StepTitle>Download the template</StepTitle>
          </ImportStepHeader>
          <TemplateDownloadButton type="button" onClick={handleDownloadTemplate}>
            <DownloadIcon size={18} />
            Download Excel Template
          </TemplateDownloadButton>
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>2</StepNumber>
            <StepTitle>Upload your completed file</StepTitle>
          </ImportStepHeader>
          <FileDropZone $hasFile={!!file}>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <UploadIcon size={28} color={COLORS.teal} />
            {file ? (
              <SelectedFileName>{file.name}</SelectedFileName>
            ) : (
              <>
                <ImageUploadText>Click to select an .xlsx file</ImageUploadText>
                <ImageUploadSubtext>Only Excel (.xlsx) files are accepted</ImageUploadSubtext>
              </>
            )}
          </FileDropZone>
        </ImportStep>

        <FormActions onCancel={onClose}>
          <FormCancelButton type="button" onClick={onClose}>Cancel</FormCancelButton>
          <FormSubmitButton type="button" onClick={handleImport} disabled={!file || importing}>
            {importing ? 'Importing...' : 'Import Items'}
          </FormSubmitButton>
        </FormActions>
      </ImportContent>
    </ImportWrapper>
  );
};

/** Sends the Add Item form to the API. Throws with a readable message on failure. */
export const submitNonAbatixItem = async (formData: AddFormData): Promise<void> => {
  const payload = new FormData();
  payload.append('location_id', formData.location_id);
  payload.append('item_id', formData.item_id);
  payload.append('description', formData.description);
  payload.append('category_id', formData.category_id);
  payload.append('quantity', formData.quantity);
  if (formData.price !== '') payload.append('price', formData.price);
  if (formData.unit_of_measure) payload.append('unit_of_measure', formData.unit_of_measure);
  if (formData.quantity_minimum !== '') payload.append('quantity_minimum', formData.quantity_minimum);
  if (formData.quantity_maximum !== '') payload.append('quantity_maximum', formData.quantity_maximum);
  if (formData.image) payload.append('image', formData.image);

  const response = await apiClientFormData('/api/non-abatix/inventory', {
    method: 'POST',
    body: payload,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to add item');
  }
};
